import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import {
  Drawer, Box, IconButton, AppBar, Toolbar, Typography, useMediaQuery,
  CssBaseline, createTheme, ThemeProvider, Fade,
  ListItemButton, ListItemIcon, ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RadioIcon from '@mui/icons-material/Radio';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';

import NavigationDrawer from './components/NavigationDrawer';

import AutoRecordConfig from './components/AutoRecordConfig';
import AutoRecordView from './components/AutoRecordView'; // 引入新的查看組件
import ManualRecording from './components/ManualRecording';
import PendingRecordings from './components/PendingRecordings';
import RecordedByTimePage from './components/RecordedByTimePage';
import RecordedByTitleStationsPage from './components/RecordedByTitleStationsPage';
import RecordedTitlesPage from './components/RecordedTitlesPage';
import RecordedEpisodesPage from './components/RecordedEpisodesPage';
import RecordedPlayerPage from './components/RecordedPlayerPage';
import SearchPage from './components/Search';
import CallbackHandler from './components/CallbackHandler';
import UnauthorizedPage from './components/UnauthorizedPage';
import ProtectedRoute from './components/ProtectedRoute';

import { AuthProvider, useAuth } from './AuthContext';
import { useAnalytics } from './hooks/useAnalytics';

const drawerWidth = 260;

// Web-radio design system tokens（見 web-radio 專案的 docs/design-system.md）
const colors = {
  bgPrimary: '#0d0d0d',
  bgSecondary: '#1a1a1a',
  bgPlayer: '#2a2a2a',
  border: '#333333',
  accent: '#e42313',
  accentMuted: 'rgba(228, 35, 19, 0.12)',
  textPrimary: '#ffffff',
  textSecondary: '#b0b0b0',
  textMuted: '#7a7a7a',
  green: '#22c55e',
};

const fontDisplay = "'Space Grotesk', 'Noto Sans TC', system-ui, -apple-system, sans-serif";
const fontMain = "'Inter', 'Noto Sans TC', system-ui, -apple-system, sans-serif";

const appTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: colors.accent },
    secondary: { main: colors.textSecondary },
    error: { main: colors.accent },
    success: { main: colors.green },
    background: {
      default: colors.bgPrimary,
      paper: colors.bgSecondary,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: colors.textMuted,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: fontMain,
    h1: { fontFamily: fontDisplay, fontWeight: 600 },
    h2: { fontFamily: fontDisplay, fontWeight: 600 },
    h3: { fontFamily: fontDisplay, fontWeight: 600 },
    h4: { fontFamily: fontDisplay, fontWeight: 600 },
    h5: { fontFamily: fontDisplay, fontWeight: 600 },
    h6: { fontFamily: fontDisplay, fontWeight: 600 },
    subtitle1: { fontFamily: fontDisplay, fontWeight: 600 },
    subtitle2: { fontFamily: fontDisplay, fontWeight: 600 },
    overline: {
      fontFamily: fontDisplay,
      fontWeight: 600,
      letterSpacing: '1px',
      color: colors.accent,
    },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '::-webkit-scrollbar': { width: 6 },
        '::-webkit-scrollbar-track': { background: colors.bgPrimary },
        '::-webkit-scrollbar-thumb': {
          background: colors.border,
          borderRadius: 3,
        },
        '::-webkit-scrollbar-thumb:hover': { background: colors.textMuted },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundImage: 'none',
          backgroundColor: colors.bgSecondary,
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: colors.bgSecondary,
          borderRight: `1px solid ${colors.border}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          border: `1px solid ${colors.border}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 9999 },
        containedPrimary: { color: '#ffffff' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'background-color 150ms',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
          '&.Mui-selected': {
            backgroundColor: colors.accentMuted,
            color: colors.accent,
            '& .MuiListItemIcon-root': { color: colors.accent },
            '&:hover': { backgroundColor: colors.accentMuted },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { color: colors.textMuted, minWidth: 36 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.textMuted },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.border },
      },
    },
  },
});

// 舊的 /recorded/by-title/:station_id 導向新的 /recorded/stations/:station_id
function LegacyStationRedirect() {
  const { station_id } = useParams();
  return <Navigate to={`/recorded/stations/${station_id}`} replace />;
}

// 舊的 /record/by-time 導向新的 /recorded，並保留原本的 query string（日期/電台等篩選）
function LegacyByTimeRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/recorded${search}`} replace />;
}

// 主應用程序內容組件
function AppContent() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, user, login, logout } = useAuth();

  // Load analytics
  useAnalytics();

  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
          <AppBar 
            position="fixed" 
            sx={{ 
              zIndex: appTheme.zIndex.drawer + 1,
              transition: 'all 0.3s ease',
              borderRadius: 0,
            }}
          >
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 1, display: { sm: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
                <RadioIcon sx={{ color: 'primary.main', mr: 1.5, display: { xs: 'none', sm: 'flex' } }} />
                <Typography variant="h6" noWrap sx={{ fontSize: 18, letterSpacing: '0.5px', color: 'primary.main' }}>
                  廣播錄製系統
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>

          <Box 
            component="nav" 
            sx={{ 
              width: { xs: 0, sm: drawerWidth }, 
              flexShrink: { sm: 0 },
              transition: 'all 0.3s ease'
            }}
          >
            <Drawer
              variant={isMobile ? 'temporary' : 'permanent'}
              open={isMobile ? mobileOpen : true}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                  transition: 'all 0.3s ease',
                },
              }}
            >
              <Toolbar sx={{ display: 'flex', alignItems: 'center', px: [1, 2] }}>
                <RadioIcon sx={{ color: 'primary.main', mx: 1.5, display: { xs: 'flex', sm: 'none' } }} />
                <Typography variant="h6" noWrap sx={{ fontSize: 18, letterSpacing: '0.5px', color: 'primary.main', display: { xs: 'flex', sm: 'none' } }}>
                  廣播錄製系統
                </Typography>
              </Toolbar>
              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <NavigationDrawer onNavigate={() => setMobileOpen(false)} />
              </Box>

              {/* 側欄底部：登入 / 使用者資訊（web-radio 側欄底部式樣） */}
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                {isAuthenticated ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2 }}>
                    <AccountCircleIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    <Typography noWrap sx={{ flexGrow: 1, fontSize: 13, color: 'text.secondary' }}>
                      {user?.name || '已登入'}
                    </Typography>
                    <IconButton size="small" aria-label="登出" onClick={logout} sx={{ color: 'text.disabled' }}>
                      <LogoutIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <ListItemButton onClick={login} sx={{ px: 3, py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LoginIcon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary="登入" primaryTypographyProps={{ fontSize: 13 }} />
                  </ListItemButton>
                )}
              </Box>
            </Drawer>
          </Box>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
              pt: 8,
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Fade in={true} timeout={500}>
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                <Routes>
                  <Route path="/" element={<Navigate to="/table" replace />} />
                  <Route path="/callback" element={<CallbackHandler />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  
                  {/* 受保護的管理員路由 */}
                  <Route path="/auto" element={
                    <ProtectedRoute requireAdmin={true}>
                      <AutoRecordConfig />
                    </ProtectedRoute>
                  } />
                  
                  {/* 公開的自動錄製規則查看路由 */}
                  <Route path="/auto-view" element={<AutoRecordView />} />
                  
                  <Route path="/table" element={<ManualRecording />} />
                  <Route path="/pending" element={<PendingRecordings />} />
                  <Route path="/search" element={<SearchPage />} />
                  {/* 已錄製內容：依日期(預設) / 依電台 → 系列(season) → 集數 */}
                  <Route path="/recorded" element={<RecordedByTimePage />} />
                  <Route path="/recorded/stations" element={<RecordedByTitleStationsPage />} />
                  <Route path="/recorded/stations/:station_id" element={<RecordedTitlesPage />} />
                  <Route path="/recorded/seasons/:season_id" element={<RecordedEpisodesPage />} />
                  <Route path="/recorded/:program_id" element={<RecordedPlayerPage />} />

                  {/* 舊路徑相容導向 */}
                  <Route path="/record/by-time" element={<LegacyByTimeRedirect />} />
                  <Route path="/record/by-title" element={<Navigate to="/recorded/stations" replace />} />
                  <Route path="/recorded/by-title/:station_id" element={<LegacyStationRedirect />} />
                  {/* 舊集數頁沒有 season_id 可對應，退回該電台的系列列表 */}
                  <Route path="/recorded/by-title/:station_id/:title" element={<LegacyStationRedirect />} />
                </Routes>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

// 主應用程序組件
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;